import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createMemo, createSignal } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { type ConnectivityLabelKey, getConnectivityUI } from "@fleet/powersync";
import { SyncIndicator } from "./sync-indicator";
import { ConnectivityModal } from "./connectivity-modal";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@mealplan/shared";

/**
 * Maps each connectivity {@link ConnectivityLabelKey} to a translated Lingui
 * descriptor. Defined HERE (a `.tsx` already in the lingui macro transform scope)
 * instead of `connectivity-ui.ts`: under rolldown-vite the macro plugin does not
 * transform `.ts` modules, so a `msg` macro there fails the build at runtime.
 */
const CONNECTIVITY_LABELS: Record<ConnectivityLabelKey, MessageDescriptor> = {
  offline: msg`Offline`,
  connected: msg`Connected`,
  checking: msg`Checking...`,
  reconnecting: msg`Reconnecting...`,
  maintenance: msg`Maintenance`,
  unauthenticated: msg`Visitor`,
};

/**
 * A pill-shaped sync status indicator used in the Profile.
 * Shows detailed status label and triggers the connectivity modal.
 * Matches the native implementation for total parity.
 */
export function SyncStatusPill(props: { class?: string }) {
  const { connectivityRef } = usePowerSyncContext();
  const snapshot = fromActorRef(connectivityRef);
  const [modalVisible, setModalVisible] = createSignal(false);
  const { t } = useI18n();

  // Get base UI from machine for the labels
  const ui = createMemo(() => getConnectivityUI(snapshot().value, snapshot().context));

  const statusContent = createMemo(() => {
    const ctx = snapshot().context;
    const isConnected = snapshot().matches("connected");

    if (isConnected && ctx.isUploading) {
      return <span>{t(msg`Syncing...`)}</span>;
    }

    if (isConnected && ctx.uploadQueueSize > 0) {
      return (
        <div class="flex items-center gap-1">
          <span>{t(msg`Queued`)}</span>
          <span class="font-bold text-amber-500">({ctx.uploadQueueSize})</span>
        </div>
      );
    }

    const label = t(CONNECTIVITY_LABELS[ui().labelKey]);
    return <span>{label}</span>;
  });

  const handlePress = (e: MouseEvent) => {
    e.stopPropagation();
    setModalVisible(true);
  };

  return (
    <div class={cn("flex flex-col items-center", props.class)}>
      <button
        type="button"
        data-testid="sync-status-pill"
        onClick={handlePress}
        class={cn(
          "flex flex-row items-center rounded-full px-4 py-1 gap-2 transition-[transform,background-color,opacity,border-color,box-shadow,color] hover:opacity-80 active:scale-95",
          ui().bgClass,
          ui().textClass,
        )}
      >
        <SyncIndicator disablePress class="p-0" />
        <div class="text-xs font-medium">{statusContent()}</div>
      </button>

      <ConnectivityModal isOpen={modalVisible()} onClose={() => setModalVisible(false)} />
    </div>
  );
}
